import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Leaderboard.css';
import { API_BASE_URL } from '../config';

function Leaderboard() {
  const navigate = useNavigate();
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterTier, setFilterTier] = useState('all');
  const [tierDistribution, setTierDistribution] = useState(null);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build query params
        const params = new URLSearchParams();
        if (filterTier !== 'all') {
          params.append('tier', filterTier);
        }
        params.append('limit', '50');

        const url = `${API_BASE_URL}/rankings/leaderboard${params.toString() ? '?' + params.toString() : ''}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Failed to fetch leaderboard');
        }

        const data = await response.json();
        setRankings(data.rankings || []);
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
        setError('Failed to load leaderboard. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [filterTier]);

  useEffect(() => {
    const fetchTierDistribution = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/rankings/stats/tiers`);
        if (response.ok) {
          const data = await response.json();
          setTierDistribution(data.distribution);
        }
      } catch (err) {
        console.error('Error fetching tier distribution:', err);
      }
    };

    fetchTierDistribution();
  }, []);

  const getTierColor = (tier) => {
    switch (tier) {
      case 'diamond':
        return '#60a5fa';
      case 'platinum':
        return '#a78bfa';
      case 'gold':
        return '#fbbf24';
      case 'silver':
        return '#94a3b8';
      case 'bronze':
        return '#cd7f32';
      default:
        return '#6b7280';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'diamond':
        return '💎';
      case 'platinum':
        return '🏆';
      case 'gold':
        return '🥇';
      case 'silver':
        return '🥈';
      case 'bronze':
        return '🥉';
      default:
        return '📊';
    }
  };

  const handleWorkerClick = (userId) => {
    navigate(`/worker/${userId}`);
  };

  return (
    <div className="leaderboard">
      <div className="leaderboard-header">
        <h1>Global Leaderboard</h1>
        <p className="leaderboard-subtitle">Top-ranked workers based on task quality and completion</p>
      </div>

      {tierDistribution && (
        <div className="tier-distribution">
          <h3>Tier Distribution</h3>
          <div className="tier-stats">
            {Object.entries(tierDistribution).map(([tier, count]) => (
              <div key={tier} className="tier-stat-item">
                <span className="tier-stat-icon">{getTierIcon(tier)}</span>
                <span className="tier-stat-name">{tier}</span>
                <span className="tier-stat-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="filters">
        <div className="filter-group">
          <label>Filter by Tier:</label>
          <select value={filterTier} onChange={(e) => setFilterTier(e.target.value)}>
            <option value="all">All Tiers</option>
            <option value="diamond">Diamond</option>
            <option value="platinum">Platinum</option>
            <option value="gold">Gold</option>
            <option value="silver">Silver</option>
            <option value="bronze">Bronze</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading leaderboard...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : rankings.length === 0 ? (
        <div className="no-rankings">No workers found in this tier</div>
      ) : (
        <div className="rankings-list">
          <div className="rankings-header-row">
            <div className="rank-column">Rank</div>
            <div className="worker-column">Worker</div>
            <div className="tier-column">Tier</div>
            <div className="stats-column">Tasks</div>
            <div className="stats-column">Quality</div>
            <div className="stats-column">Pass Rate</div>
            <div className="points-column">Points</div>
          </div>
          {rankings.map((ranking) => (
            <div
              key={ranking.id}
              className="ranking-item"
              onClick={() => handleWorkerClick(ranking.user_id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="rank-column">
                <div className="rank-number">#{ranking.rank}</div>
              </div>
              <div className="worker-column">
                <div className="worker-name">{ranking.users?.username || 'Worker'}</div>
                {ranking.streak_bonus > 0 && (
                  <div className="streak-indicator">
                    🔥 {ranking.streak_bonus} streak
                  </div>
                )}
              </div>
              <div className="tier-column">
                <div className="tier-badge" style={{ borderColor: getTierColor(ranking.rank_tier) }}>
                  <span className="tier-badge-icon">{getTierIcon(ranking.rank_tier)}</span>
                  <span className="tier-badge-name">{ranking.rank_tier.toUpperCase()}</span>
                </div>
              </div>
              <div className="stats-column">
                <div className="stat-value">{ranking.total_tasks_completed}</div>
              </div>
              <div className="stats-column">
                <div className="stat-value">{ranking.average_quality_score.toFixed(1)}</div>
              </div>
              <div className="stats-column">
                <div className="stat-value">{ranking.average_test_pass_rate.toFixed(1)}%</div>
              </div>
              <div className="points-column">
                <div className="points-value">{ranking.rank_points}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Leaderboard;
